'use client';
import { useEffect, useMemo, useState } from 'react';
import { PostForm } from './components/post-form';
import { PostList } from './components/posts/post-list';
import {
  IConversationSet,
  SearchCommand,
  ThreadCounterPill,
  useConversationContext,
  useMessageContext,
} from '@ui/components';
import { useLoader } from '@ui/contexts';
import { CommunitySidebar } from './components/community-sidebar';
import { usePostHogFeatureFlag } from '@ui/hooks';
import { useSearchParams } from 'next/navigation';

interface ICreatorCommunityViewProp {
  viewType: 'community' | 'topic';
  showBreadcrumbs: boolean;
  showResourceNavigationTitle: boolean;
  isTopicSelected: boolean;
  getCounter: (threadId: string) => number;
  subscribe: (callback: () => void) => () => void;
  resetCounter: (threadId: string) => void;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
  onViewReports: () => void;
  reportsDrawer?: React.ReactNode;
  userId?: string;
}
export const CreatorCommunityView = (props: ICreatorCommunityViewProp) => {
  const isModerationEnabled = usePostHogFeatureFlag('community-moderation');
  const conversationState = useConversationContext();
  const [open, setOpen] = useState(false);
  const messageState = useMessageContext('creator');
  const { openLoader, closeLoader } = useLoader();
  const params = useSearchParams();
  const enableSearchFeature = usePostHogFeatureFlag('enable-search-feature');

  useEffect(() => {
    const hasSearchQuery = params.get('search_query');
    if (hasSearchQuery) {
      setOpen(true);
    }
  }, [params.get('search_query')]);

  useEffect(() => {
    openLoader();

    const shouldCreateCommunity =
      conversationState.communitySets.length === 0 &&
      conversationState.isCommunityConversationSuccess &&
      !conversationState.isLoadingCommunityConversations;

    if (shouldCreateCommunity && props?.viewType == 'community') {
      conversationState.onCreateConversation({
        creator_id: conversationState.creator_id,
        name: 'Default Community',
        description: 'A dynamic space for creators and fans to share, connect, and grow together.',
        conversation_type: 'community',
        restrictions: {
          fans_react: true,
          fans_reply: true,
          fans_report: true,
        },
        public: true,
        ready: true,
      });

      return;
    }

    if (conversationState.communitySets.length > 0 && props?.viewType == 'community') {
      const communitySet = conversationState.communitySets[0];
      messageState.setSetQuery({
        ...messageState.query,
        creator_id: communitySet.conversation.creator_id,
        thread_id: communitySet.conversation.main_thread,
        conversation_id: conversationState.communitySets[0]?.conversation?.id,
        fan_id: messageState.user_id,
        page: 1,
        draft: false,
        ordering: 'desc',
        conversation_type: conversationState.communitySets[0]?.conversation?.conversation_type,
        created_by_creator: true,
      });
      conversationState.onSelectConversation(communitySet);
      closeLoader();
    }
    closeLoader();
  }, [
    conversationState.isCommunityConversationSuccess,
    conversationState.isLoadingCommunityConversations,
    conversationState.communitySets.length,
  ]);

  useEffect(() => {
    if (conversationState.singleConversationSet && props?.viewType == 'topic') {
      const topicSet = conversationState.singleConversationSet;
      messageState.setSetQuery({
        ...messageState.query,
        creator_id: topicSet.conversation.creator_id,
        thread_id: topicSet.conversation.main_thread,
        conversation_id: topicSet.conversation.id,
        fan_id: messageState.user_id,
        page: 1,
        draft: false,
        ordering: 'desc',
        conversation_type: 'topic',
      });
      conversationState.onSelectConversation(topicSet);
      closeLoader();
    }
  }, [
    conversationState.isSingleConversationSuccess,
    conversationState.isSingleConversationLoading,
    conversationState.singleConversationSet,
    conversationState.communitySets[0]?.conversation?.conversation_type,
    conversationState.communitySets[0]?.conversation?.id,
  ]);

  const threadIdToSubscribeTo = useMemo(() => {
    if (conversationState.singleConversationSet && props?.viewType == 'topic') {
      return conversationState.singleConversationSet.conversation.main_thread;
    }
    const communitySet = conversationState?.communitySets?.[0];
    return communitySet?.conversation?.main_thread;
  }, [
    conversationState.singleConversationSet,
    conversationState?.communitySets?.[0]?.conversation?.main_thread,
  ]);

  return (
    <div
      className={`mx-auto flex w-full flex-col lg:flex-row ${
        isModerationEnabled ? 'lg:max-w-[1200px] lg:gap-6' : 'lg:max-w-[800px]'
      }`}>
      <div className="flex-1 w-full">
        <div className="flex flex-col gap-4">
          {conversationState?.singleConversationSet && (
            <div className="flex flex-col gap-4 lg:hidden">
              <h1 className="text-2xl font-bold">
                {conversationState.singleConversationSet?.conversation.name}
              </h1>
              <p className="text-sm text-gray-500">
                {conversationState.singleConversationSet?.conversation.description}
              </p>
            </div>
          )}

          <PostForm creatorData={props.creatorData} />

          {threadIdToSubscribeTo && (
            <ThreadCounterPill
              threadId={threadIdToSubscribeTo}
              onPress={() => {
                messageState?.refetchMessages?.();
              }}
              label="New post"
              variant="success"
              getCounter={props.getCounter}
              subscribe={props.subscribe}
              resetCounter={props.resetCounter}
              className="sticky top-4 left-4 right-4 z-50 mx-auto justify-center"
            />
          )}

          <PostList
            defaultCommunitySet={conversationState.communitySets[0]}
            communitySet={conversationState.singleConversationSet as IConversationSet}
            hasCreatorEnabledTopics={
              conversationState.communitySets[0]?.restrictions_list?.creator_enabled_topics || false
            }
            hasCreatorEnabledFansCanCreatePosts={
              conversationState.communitySets[0]?.restrictions_list?.fans_can_create_posts || false
            }
            communityOwnerSlug={messageState.creator_info.name}
            defaultCommunitySlug={conversationState.communitySets[0]?.conversation?.slug || ''}
            communitySets={
              props.viewType == 'community'
                ? conversationState.communitySets
                : conversationState.singleConversationSet
                ? [conversationState.singleConversationSet]
                : []
            }
            isCommunityConversationSuccess={
              props.viewType == 'community'
                ? conversationState.isCommunityConversationSuccess
                : conversationState.isSingleConversationSuccess
            }
            isLoadingCommunityConversations={
              props.viewType == 'community'
                ? conversationState.isLoadingCommunityConversations
                : conversationState.isSingleConversationLoading
            }
            topics={props.viewType == 'community' ? conversationState.topicSets : []}
            isCommunityView={props?.viewType == 'community' ? true : false}
            conversationState={conversationState}
            userType="creator"
            emptyState={{
              title: 'No posts yet',
              message:
                'It looks a bit empty here. Start by creating your first post to engage with your fans!',
            }}
            showBreadcrumbs={props.showBreadcrumbs}
            showResourceNavigationTitle={props.showResourceNavigationTitle}
            onSearchIconClick={() => setOpen(true)}
            isTopicSelected={props.isTopicSelected}
            creatorData={props.creatorData}
            onViewReports={props.onViewReports}
          />
        </div>
      </div>

      {isModerationEnabled && (
        <div className="flex-shrink-0 w-full lg:w-auto mt-6 lg:mt-0 hidden lg:block">
          <div className="lg:sticky lg:top-4">
            <CommunitySidebar
              userType="creator"
              communitySet={
                props.viewType == 'community'
                  ? conversationState.communitySets[0]
                  : conversationState.singleConversationSet
              }
              masterCommunitySet={conversationState.communitySets[0]}
              isCommunitySuccess={
                conversationState.isCommunityConversationSuccess ||
                conversationState.isSingleConversationSuccess
              }
              isLoadingCommunity={
                conversationState.isLoadingCommunityConversations ||
                conversationState.isSingleConversationLoading
              }
              userId={props.userId || messageState.user_id}
              onViewReports={props.onViewReports}
            />
          </div>
        </div>
      )}

      <div>
        {open && enableSearchFeature && (
          <SearchCommand
            items={messageState?.searchConversationData?.community ?? []}
            onClose={() => setOpen(false)}
            onInputChange={(text) => {
              if (conversationState.creator_id) {
                messageState?.communitySearch?.(text, conversationState.creator_id);
              }
            }}
            placeholder="Search post on community"
            isLoading={messageState?.isSearchConversationDataLoading}
            isOpen={open}
            searchSet={conversationState.communitySets ?? conversationState.singleConversationSet}
            searchDomain="community"
            userType="creator"
            totalValue={messageState?.searchConversationData?.total_values ?? 0}
          />
        )}
      </div>
      {props.reportsDrawer && props.reportsDrawer}
    </div>
  );
};
